import { useAuth0 } from '@auth0/auth0-react';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { BottomSheet } from 'components/BottomSheet';
import { Profile } from './Profile';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fires after the slide-down finishes — used to pop the /account route. */
  onExited?: () => void;
}

const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
} as const;

const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }
  return email ? email[0].toUpperCase() : '';
};

/**
 * The account/profile menu presented as a bottom sheet. Driven by the `/account` route
 * (so the Profile menu's relative navigation resolves to `/account/...`); the blue
 * header sits flush at the top so the sheet's rounded corners clip it, and the menu
 * scrolls beneath it.
 */
export const AccountSheet = ({ open, onClose, onExited }: Props) => {
  const { user } = useAuth0();

  return (
    <BottomSheet open={open} onClose={onClose} onExited={onExited}>
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          px: 3,
          pt: 3.5,
          pb: 3
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          data-testid="account-sheet-close"
          sx={{ position: 'absolute', top: 6, right: 6, color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 20,
              flexShrink: 0
            }}
          >
            {getInitials(user?.name, user?.email)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              data-dd-privacy="mask"
              sx={{ color: '#fff', fontWeight: 700, ...ellipsis }}
            >
              {user?.name}
            </Typography>
            <Typography
              variant="body2"
              data-dd-privacy="mask"
              sx={{ color: 'rgba(255, 255, 255, 0.85)', ...ellipsis }}
            >
              {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 2 }}>
        <Profile withTopOffset={false} />
      </Box>
    </BottomSheet>
  );
};
